import { useState, useEffect } from 'react';
import { getPageBySlug } from '../services/contentstack';
import { ContentstackPage } from '../types/contentstack';
import { useTranslation } from './useTranslation';

/**
 * Hook to fetch a page by slug from Contentstack
 * @param slug - The page slug to fetch
 * @param locale - Optional locale override (defaults to current locale from context)
 * @returns Object with page data, loading state, and error
 */
export function useContentstackPage(slug: string, locale?: string) {
  const { locale: contextLocale } = useTranslation();
  const [data, setData] = useState<ContentstackPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const effectiveLocale = locale || contextLocale;

  useEffect(() => {
    let isMounted = true;

    async function fetchPage() {
      try {
        setLoading(true);
        setError(null);
        const normalizedLocale = effectiveLocale.toLowerCase();
        const pageData = await getPageBySlug(slug, normalizedLocale);
        if (isMounted) {
          setData(pageData);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to fetch page'));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    if (slug) {
      fetchPage();
    }

    return () => {
      isMounted = false;
    };
  }, [slug, effectiveLocale]);

  return { data, loading, error };
}
